import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/utils';
import { useWavesurfer } from '@wavesurfer/react';
import { LoadingBar } from '../LoadingBar';
import { PlayerControl } from './PlayerControl';
import { SkipBack, SkipForward } from 'lucide-react';
import { PlayModeButton } from './PlayerModeButton';
import { updatePlayTime } from '@/service/audioList';
import { AudioDto } from '@/service/type';
import { AudioDetailDto } from '@/service/interface';
import { UploadAudio } from '@/state/audio-player-store';

interface PlayerUIProps {
  onFinish?: () => void;
  setIsPlaying: (isPlaying: boolean) => void;
  isPlaying: boolean;
  playUrl: string;
  className?: string;
  onPre: () => void;
  onNext: () => void;
  playAudioInfo: AudioDto | AudioDetailDto | null | UploadAudio;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  setWave: (wave: ReturnType<typeof useWavesurfer>) => void;
  setCurrentTime: (time: number) => void;
}

export const PlayerUI: React.FC<PlayerUIProps> = ({
  onFinish,
  playUrl,
  setIsPlaying,
  isPlaying,
  className,
  onPre,
  onNext,
  playAudioInfo,
  isLoading,
  setIsLoading,
  setWave,
  setCurrentTime,
}) => {
  const containerRef = useRef(null);
  // const audioRef = useRef<HTMLAudioElement | null>(null); // Reference for the audio element

  const wave = useWavesurfer({
    container: containerRef,
    barGap: 2,
    barWidth: 2,
    barHeight: 0.9,
    barRadius: 1,
    barAlign: 'bottom',
    height: 'auto',
    cursorWidth: 0,
    cursorColor: 'transparent',
    fillParent: true,
    mediaControls: false,
    autoplay: false,
    interact: true,
    dragToSeek: true,
    progressColor: '#FF5B27',
    waveColor: '#404040',
    normalize: false,
    sampleRate: 48000,
  });
  const { wavesurfer, isPlaying: isWavePlaying, currentTime } = wave;

  const [hasCountPlay, setHasCountPlay] = useState(false);

  useEffect(() => {
    setWave(wave);
  }, [wave]);

  useEffect(() => {
    setIsPlaying(isWavePlaying);
  }, [isWavePlaying]);

  useEffect(() => {
    setCurrentTime(currentTime);
    if (Math.floor(currentTime) > 3) {
      if (!hasCountPlay) {
        addPlayCounter();
      }
    } else {
      setHasCountPlay(false);
    }
  }, [currentTime, hasCountPlay]);

  const addPlayCounter = async () => {
    setHasCountPlay(true);
    if (playAudioInfo?.id && playAudioInfo?.id?.length === 36) {
      await updatePlayTime({ playingId: playAudioInfo?.id });
    }
  };

  useEffect(() => {
    let unsubscribeFinish: () => void = () => {};
    let unsubscribeError: () => void = () => {};
    if (wavesurfer) {
      unsubscribeFinish = wavesurfer.on('finish', () => {
        onFinish?.();
        wavesurfer.setTime(0);
      });
      unsubscribeError = wavesurfer.on('error', (e) => {
        setIsLoading(false);
        console.error('audio player error:', e);
      });
    }
    return () => {
      unsubscribeFinish();
      unsubscribeError();
    };
  }, [wavesurfer, onFinish]);
  return (
    <div className={cn('hidden bg-jen-grey-3 p-2 relative w-full h-[82px] lg:h-[88px]', className)}>
      <div className="flex h-full items-center rounded bg-jen-grey-3 lg:bg-black px-2 py-1 justify-between">
        {
          <div className=" truncate flex items-center justify-between h-[44px] w-[40%] lg:w-[20%] ml-2">
            {playUrl ? (
              <div className="w-full">
                <span className="w-full block truncate text-white line-clamp-1 text-base">{playAudioInfo?.name}</span>
                <span className="w-full block truncate text-white line-clamp-1 text-sm opacity-50">
                  {playAudioInfo && 'user_name' in playAudioInfo ? playAudioInfo.user_name : ''}
                </span>
              </div>
            ) : (
              <span className="text-white/30 line-clamp-1">No Track Selected</span>
            )}
          </div>
        }
        <div ref={containerRef} className={cn('lg:block relative h-16 ml-2 px-1 flex-1 rounded overflow-hidden')}>
          <div className="absolute dotted-waveform transition-all z-30 w-full h-full"></div>
          {isLoading && <LoadingBar className="w-full h-14" />}
        </div>

        <div
          className=" absolute bottom-0 left-0 h-1 bg-jen-orange block lg:hidden "
          style={{
            width: `${playAudioInfo?.duration ? ((currentTime / playAudioInfo?.duration) * 100).toFixed(2) : 0}%`,
          }}
        />
        {
          <div className={cn('flex flex-col items-center ml-2 w-[50%] lg:w-[20%]', playUrl ? '' : 'opacity-40')}>
            <div className="hidden lg:block text-xl text-white mr-[40px]">{formatTime(currentTime)}</div>
            <div className="flex items-center text-white">
              <div
                className="w-10 h-10 flex justify-center items-center "
                onClick={() => {
                  onPre();
                }}
              >
                <SkipBack className={'lg:block text-white cursor-pointer h-6 w-6 mr-1'} fill="#fff" />
              </div>
              <div
                className="w-10 h-10 flex justify-center items-center"
                onClick={() => {
                  wavesurfer?.playPause();
                }}
              >
                <PlayerControl isPlaying={isPlaying} isLoading={isLoading} />
              </div>

              <div
                onClick={() => {
                  onNext();
                }}
                className="w-10 h-10 flex justify-center items-center"
              >
                <SkipForward className={'lg:block text-white cursor-pointer h-6 w-6 ml-0.5'} fill="#fff" />
              </div>
              <div className=" cursor-pointer w-10 h-10 flex justify-center items-center">
                <PlayModeButton />
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  );
};

const formatTime = (time: number) => {
  return [
    String(Math.floor((time % 3600) / 60)).padStart(2, '0'), // minutes
    ('00' + Math.floor(time % 60)).slice(-2), // seconds
  ].join(':');
};
